import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

export const EASE = [0.22, 1, 0.36, 1];

export function Reveal({ children, delay = 0, y = 28, className = "" }) {
    return (
        <motion.div
            className={className}
            initial={{ opacity: 0, y }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay, ease: EASE }}
        >
            {children}
        </motion.div>
    );
}

export function MaskedLines({ lines, className = "", lineClassName = "", as: Tag = "h1", stagger = 0.12, accentIndex = -1 }) {
    return (
        <Tag className={className}>
            {lines.map((line, i) => (
                <span key={i} className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
                    <motion.span
                        className={`block ${i === accentIndex ? "text-accent" : ""} ${lineClassName}`}
                        initial={{ y: "112%" }}
                        animate={{ y: "0%" }}
                        transition={{ duration: 1, delay: 0.15 + i * stagger, ease: EASE }}
                    >
                        {line}
                    </motion.span>
                </span>
            ))}
        </Tag>
    );
}

export function Chapter({ index, label, className = "" }) {
    return (
        <Reveal className={`flex items-center gap-4 ${className}`}>
            <span className="font-mono-label text-accent">{String(index).padStart(2, "0")}</span>
            <span className="h-px w-10 bg-accent/60" aria-hidden="true" />
            <span className="font-mono-label text-steel">{label}</span>
        </Reveal>
    );
}

export function Breadcrumbs({ items }) {
    return (
        <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1.5 text-sm text-steel" data-testid="breadcrumbs">
            {items.map((item, i) => (
                <React.Fragment key={i}>
                    {i > 0 && <ChevronRight className="h-3.5 w-3.5 text-slate-300" aria-hidden="true" />}
                    {item.to ? (
                        <Link to={item.to} data-testid={`breadcrumb-${item.label.toLowerCase().replace(/\s+/g, "-")}`} className="hover:text-accent transition-colors duration-200">
                            {item.label}
                        </Link>
                    ) : (
                        <span className="text-navy font-medium" aria-current="page">{item.label}</span>
                    )}
                </React.Fragment>
            ))}
        </nav>
    );
}

export function Placeholder({ label }) {
    return (
        <div className="rounded-lg border border-dashed border-slate-300 bg-mist px-4 py-3 text-sm text-steel" data-testid="info-placeholder">
            <span className="font-mono-label text-slate-400 mr-2">Placeholder</span>
            {label} — to be updated once details are provided.
        </div>
    );
}
